import classNames from "classnames";
import { FunctionComponent } from "react";
import { useIntl } from "react-intl";
import ButtonSmily from "@Components/atoms/ButtonSmily";
import { useBreakpoint } from "@Hooks/useBreakpoint";
import DraftContent from "@Types/draft";
import { goToCustomization } from "@Utils/navigation";
import { isProductEditable } from "@Utils/product";
import { setDataLayerTrack } from "@Utils/track";
import "./style.scss";

interface Props {
  draft: DraftContent;
  onRemoveDraft: (draft: DraftContent) => void;
}

const DraftItem: FunctionComponent<Props> = ({ draft, onRemoveDraft }) => {
  const intl = useIntl();
  const { isSmallScreen } = useBreakpoint();

  const handleClickDelete = () => {
    onRemoveDraft(draft);
  };

  const handleClickEdit = () => {
    if (isProductEditable(draft.product)) {
      setDataLayerTrack("Saved Products Continue Clicked Data", {
        productTag: draft.product.tag,
      });
      goToCustomization(intl.locale, draft.id);
    }
  };

  const timeSinceCreation = (): string => {
    const viewDate = new Date();
    const creationDate = new Date(draft.updatedAt);
    const millisecondsSinceCreation = viewDate.getTime() - creationDate.getTime();

    switch (true) {
      // Less than 1 minute
      case millisecondsSinceCreation < 60000:
        return intl.formatMessage({ id: "cheerz_Drafts_save_time_less_one_minute" });
      // Between 1 and 2 minutes
      case millisecondsSinceCreation < 60000 * 2:
        return intl.formatMessage({ id: "cheerz_Drafts_save_time_one_minute" });
      // Between 2 and 60 minutes
      case millisecondsSinceCreation < 60000 * 60:
        return intl.formatMessage(
          { id: "cheerz_Drafts_save_time_minutes" },
          { value: Math.round(millisecondsSinceCreation / 60000) }
        );
      // Between 1 and 2 hours
      case millisecondsSinceCreation < 60000 * 60 * 2:
        return intl.formatMessage({ id: "cheerz_Drafts_save_time_one_hour" });
      // Between 2 and 24 hours
      case millisecondsSinceCreation < 60000 * 60 * 24:
        return intl.formatMessage(
          { id: "cheerz_Drafts_save_time_hours" },
          { value: Math.round(millisecondsSinceCreation / (60000 * 60)) }
        );
      // Between 1 and 2 days
      case millisecondsSinceCreation < 60000 * 60 * 24 * 2:
        return intl.formatMessage({ id: "cheerz_Drafts_save_time_one_day" });
      // Between 2 and 30 days
      case millisecondsSinceCreation < 60000 * 60 * 24 * 30:
        return intl.formatMessage(
          { id: "cheerz_Drafts_save_time_days" },
          { value: Math.round(millisecondsSinceCreation / (60000 * 60 * 24)) }
        );
      // Between 1 and 2 months
      case millisecondsSinceCreation < 60000 * 60 * 24 * 30 * 2:
        return intl.formatMessage({ id: "cheerz_Drafts_save_time_one_month" });
      // More than 2 months
      default:
        return intl.formatMessage(
          { id: "cheerz_Drafts_save_time_months" },
          {
            value:
              (viewDate.getFullYear() - creationDate.getFullYear()) * 12 -
              creationDate.getMonth() +
              viewDate.getMonth(),
          }
        );
    }
  };

  return (
    <div
      className={classNames("DraftItem", {
        "DraftItem--editable": isProductEditable(draft.product),
      })}
    >
      <div className="DraftItem-picture" onClick={handleClickEdit}>
        {draft.product.outOfStock && (
          <>
            <div className="DraftItem-picture-overlay" />
            <div className="DraftItem-picture-alert">
              {intl.formatMessage({ id: "cheerz_Drafts_out_of_stock" })}
            </div>
          </>
        )}
        <img
          alt={draft.product.name}
          className="DraftItem-picture-image"
          src={draft.product.image}
        />
      </div>
      <div className="DraftItem-content">
        <div className="DraftItem-content-name" onClick={handleClickEdit}>
          {draft.product.name}
        </div>
        <div className="DraftItem-content-timeSinceCreation">{timeSinceCreation()}</div>
        <div className="DraftItem-content-buttons">
          {isProductEditable(draft.product) && (
            <ButtonSmily onClick={handleClickEdit} size={isSmallScreen ? "xs" : "s"} type="primary">
              {intl.formatMessage({ id: "cheerz_Drafts_edit_button" })}
            </ButtonSmily>
          )}
          <ButtonSmily
            onClick={handleClickDelete}
            size={isSmallScreen ? "xs" : "s"}
            type="tertiary"
          >
            {intl.formatMessage({ id: "cheerz_Drafts_delete_button" })}
          </ButtonSmily>
        </div>
      </div>
    </div>
  );
};

export default DraftItem;
